// src/hooks/useTurnstile.js

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for Cloudflare Turnstile integration
 * Activates for all restaurants
 */
const useTurnstile = (restaurantId) => {
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileLoading, setTurnstileLoading] = useState(false);
  const [turnstileError, setTurnstileError] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const widgetIdRef = useRef(null);
  const containerRef = useRef(null);
  
  const SITE_KEY = '0x4AAAAAABfa0RdIrnBlBod-';

  // Check if Turnstile should be active (now active for all restaurants)
  useEffect(() => {
    const effectiveRestaurantId = restaurantId || window.restaurantId;
    const shouldActivate = !!effectiveRestaurantId; // Active for any restaurant ID
    setIsActive(shouldActivate);
    
    console.log('Turnstile activation check:', {
      restaurantId: effectiveRestaurantId,
      shouldActivate,
      turnstileScriptExists: !!window.turnstile,
      turnstileScriptTag: !!document.querySelector('script[src*="turnstile"]')
    });
    
    // Check if Turnstile script is loaded in HTML and load it if missing
    if (shouldActivate) {
      const scriptTag = document.querySelector('script[src*="turnstile"]');
      if (!scriptTag) {
        console.warn('Turnstile script not found in HTML! Attempting to load dynamically...');
        
        // Load Turnstile script dynamically
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.defer = true;
        script.onload = () => {
          console.log('Turnstile script loaded dynamically');
        };
        script.onerror = () => {
          console.error('Failed to load Turnstile script dynamically');
          setTurnstileError('Kon Turnstile script niet laden.');
        };
        document.head.appendChild(script);
      }
    }
  }, [restaurantId]);

  // Initialize Turnstile widget
  const initializeTurnstile = useCallback(() => {
    if (!isActive) {
      console.log('Turnstile not active, skipping initialization');
      return;
    }

    if (!window.turnstile) {
      console.log('Turnstile script not loaded yet, will retry...');
      return;
    }

    try {
      console.log('Initializing Turnstile widget...');
      setTurnstileLoading(false); // Don't set loading during init
      setTurnstileError(null);

      // Create invisible container if it doesn't exist
      if (!containerRef.current) {
        containerRef.current = document.createElement('div');
        containerRef.current.style.display = 'none';
        containerRef.current.id = 'turnstile-container';
        document.body.appendChild(containerRef.current);
      }

      // Clean up any existing widget
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          console.warn('Error removing existing widget:', e);
        }
        widgetIdRef.current = null;
      }

      // Render invisible Turnstile widget
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        size: 'invisible',
        callback: (token) => {
          console.log('Turnstile token received during initialization');
          setTurnstileToken(token);
          setTurnstileLoading(false);
          setTurnstileError(null);
        },
        'error-callback': (error) => {
          console.error('Turnstile error during initialization:', error);
          setTurnstileError('Veiligheidsverificatie mislukt. Probeer opnieuw.');
          setTurnstileLoading(false);
        },
        'expired-callback': () => {
          console.log('Turnstile token expired');
          setTurnstileToken(null);
          setTurnstileError(null); // Don't show error for expiration
        },
        'timeout-callback': () => {
          console.log('Turnstile timeout during initialization');
          setTurnstileError('Veiligheidsverificatie time-out. Probeer opnieuw.');
          setTurnstileLoading(false);
        }
      });

      if (widgetIdRef.current) {
        console.log('Turnstile widget created with ID:', widgetIdRef.current);
      } else {
        throw new Error('Failed to create Turnstile widget');
      }

    } catch (error) {
      console.error('Failed to initialize Turnstile:', error);
      setTurnstileError('Kon veiligheidsverificatie niet laden.');
      setTurnstileLoading(false);
    }
  }, [isActive]);

  // Execute Turnstile challenge
  const executeTurnstile = useCallback(async () => {
    if (!isActive) {
      console.log('Turnstile not active for this restaurant');
      return Promise.resolve(null);
    }

    if (!window.turnstile) {
      console.error('Turnstile script not loaded');
      setTurnstileError('Veiligheidsverificatie niet beschikbaar.');
      return Promise.reject(new Error('Turnstile script not loaded'));
    }

    // If no widget exists, try to initialize first
    if (!widgetIdRef.current) {
      console.log('No widget found, attempting to initialize...');
      initializeTurnstile();
      
      // Wait a bit for initialization
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (!widgetIdRef.current) {
        console.error('Failed to initialize Turnstile widget');
        setTurnstileError('Veiligheidsverificatie niet beschikbaar.');
        return Promise.reject(new Error('Turnstile not initialized'));
      }
    }

    return new Promise(async (resolve, reject) => {
      try {
        console.log('Executing Turnstile challenge...');
        setTurnstileLoading(true);
        setTurnstileError(null);

        // If we already have a valid token, return it
        if (turnstileToken && !window.turnstile.isExpired(widgetIdRef.current)) {
          console.log('Using existing valid token');
          setTurnstileLoading(false);
          resolve(turnstileToken);
          return;
        }

        // Reset widget if needed
        if (turnstileToken || window.turnstile.isExpired(widgetIdRef.current)) {
          console.log('Resetting expired widget');
          try {
            window.turnstile.reset(widgetIdRef.current);
          } catch (resetError) {
            console.warn('Error resetting widget:', resetError);
            // Try to recreate the widget
            initializeTurnstile();
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          setTurnstileToken(null);
        }

        // Set up timeout for execution
        const timeout = setTimeout(() => {
          console.error('Turnstile execution timeout');
          setTurnstileError('Veiligheidsverificatie time-out. Probeer opnieuw.');
          setTurnstileLoading(false);
          reject(new Error('Turnstile execution timeout'));
        }, 30000); // 30 second timeout

        // Set up temporary callback for this execution
        const originalCallback = (token) => {
          clearTimeout(timeout);
          console.log('Turnstile execution completed with token');
          setTurnstileToken(token);
          setTurnstileLoading(false);
          resolve(token);
        };

        const originalErrorCallback = (error) => {
          clearTimeout(timeout);
          console.error('Turnstile execution failed:', error);
          setTurnstileError('Veiligheidsverificatie mislukt. Probeer opnieuw.');
          setTurnstileLoading(false);
          reject(new Error('Turnstile verification failed'));
        };

        // Re-render with execution callbacks
        if (containerRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (removeError) {
            console.warn('Error removing widget for re-render:', removeError);
          }
          
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: SITE_KEY,
            size: 'invisible',
            execution: 'render', // Execute immediately
            callback: originalCallback,
            'error-callback': originalErrorCallback,
            'expired-callback': () => {
              clearTimeout(timeout);
              console.log('Token expired during execution');
              setTurnstileToken(null);
              reject(new Error('Token expired'));
            },
            'timeout-callback': () => {
              clearTimeout(timeout);
              console.log('Turnstile timeout during execution');
              setTurnstileError('Veiligheidsverificatie time-out. Probeer opnieuw.');
              setTurnstileLoading(false);
              reject(new Error('Turnstile timeout'));
            }
          });
        } else {
          clearTimeout(timeout);
          reject(new Error('No container available'));
        }

      } catch (error) {
        console.error('Error executing Turnstile:', error);
        setTurnstileError('Veiligheidsverificatie fout.');
        setTurnstileLoading(false);
        reject(error);
      }
    });
  }, [isActive, turnstileToken, initializeTurnstile]);

  // Reset Turnstile widget
  const resetTurnstile = useCallback(() => {
    if (isActive && window.turnstile && widgetIdRef.current) {
      console.log('Resetting Turnstile widget');
      window.turnstile.reset(widgetIdRef.current);
      setTurnstileToken(null);
      setTurnstileError(null);
    }
  }, [isActive]);

  // Initialize when Turnstile script loads
  useEffect(() => {
    if (!isActive) return;

    let retryCount = 0;
    const maxRetries = 50; // 5 seconds with 100ms intervals
    
    const checkTurnstileReady = () => {
      if (window.turnstile) {
        console.log('Turnstile script loaded, initializing...');
        initializeTurnstile();
      } else if (retryCount < maxRetries) {
        retryCount++;
        console.log(`Waiting for Turnstile script to load... (${retryCount}/${maxRetries})`);
        setTimeout(checkTurnstileReady, 100);
      } else {
        console.error('Turnstile script failed to load after maximum retries');
        setTurnstileError('Kon veiligheidsverificatie script niet laden.');
      }
    };

    // Start checking immediately
    checkTurnstileReady();

    // Cleanup
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (error) {
          console.warn('Error cleaning up Turnstile:', error);
        }
      }
      if (containerRef.current && containerRef.current.parentNode) {
        containerRef.current.parentNode.removeChild(containerRef.current);
      }
    };
  }, [isActive, initializeTurnstile]);

  // Debug function to help troubleshoot
  const debugInfo = useCallback(() => {
    console.log('=== Turnstile Debug Info ===');
    console.log('isActive:', isActive);
    console.log('restaurantId:', restaurantId || window.restaurantId);
    console.log('turnstile script loaded:', !!window.turnstile);
    console.log('turnstile script tag exists:', !!document.querySelector('script[src*="turnstile"]'));
    console.log('widgetId:', widgetIdRef.current);
    console.log('container exists:', !!containerRef.current);
    console.log('current token:', turnstileToken);
    console.log('loading state:', turnstileLoading);
    console.log('error state:', turnstileError);
    console.log('==========================');
  }, [isActive, restaurantId, turnstileToken, turnstileLoading, turnstileError]);

  const hasValidToken = !!turnstileToken && 
    (!widgetIdRef.current || !window.turnstile?.isExpired(widgetIdRef.current));

  return {
    isActive,
    turnstileToken,
    turnstileLoading,
    turnstileError,
    executeTurnstile,
    resetTurnstile,
    hasValidToken,
    debugInfo
  };
};

export default useTurnstile;
